import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    // Load only the source matching current device (phone vs desktop)
    if (videoSources) {
      for (const source of videoSources) {
        const { videoSource } = source.dataset;
        if (videoSource) {
          const media = source.getAttribute('media');
          if (!media || window.matchMedia(media).matches) {
            source.setAttribute('src', videoSource);
          }
        }
      }
    }

    if (videoElement) {
      videoElement.load();
      // Play default audio
      videoElement.muted = false;
      const playPromise = videoElement.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Fall back to muted autoplay if browser policy restricts unmuted autoplay
          videoElement.muted = true;
          videoElement.play().catch(() => {});
        });
      }
    }

    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  handleVisibilityChange() {
    const { videoElement } = this.refs;
    if (!videoElement) return;

    if (document.hidden) {
      // Tab is inactive / background -> auto mute & pause
      this._wasMutedBeforeHidden = videoElement.muted;
      videoElement.muted = true;
      videoElement.pause();
    } else {
      // Tab is active back -> resume video playback and restore sound preference
      videoElement.play().catch(() => {});
      if (this._wasMutedBeforeHidden === false) {
        videoElement.muted = false;
      }
    }
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
